"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { Modal } from "@/components/modal/modal";
import { Input } from "@/components/input";
import { Button } from "@/components/button";
import { userService } from "@/services/user";
import { error, success } from "@/components/alert";
import NetworkError from "@/utils/network-error";
import { ChangePasswordBody } from "@/types/user";
import { passwordSchema } from "@/utils/password-validation";

const schema = yup.object({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: passwordSchema.required("New password is required"),
  confirmNewPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

type FormData = yup.InferType<typeof schema>;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal = ({
  isOpen,
  onClose,
}: ChangePasswordModalProps) => {
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (body: ChangePasswordBody) => userService.changePassword(body),
    onSuccess: () => {
      success("Password updated successfully");
      reset();
      setApiError(null);
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof NetworkError
          ? err.message
          : "Failed to update password. Please try again.";
      setApiError(message);
      error(message);
    },
  });

  const onSubmit = (data: FormData) => {
    setApiError(null);
    mutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  const handleClose = () => {
    reset();
    setApiError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      withCloseButton
      size="small"
      fixedButton
    >
      <div className="p-6 bg-white">
        <h2 className="text-xl font-bold mb-2">Change Password</h2>
        <p className="text-gray-500 text-sm mb-6">
          Update your password to keep your account secure
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            {...register("currentPassword")}
            type="password"
            fullWidth
            label="Current password"
            placeholder="Enter current password"
            error={errors.currentPassword?.message}
            required
          />

          <Input
            {...register("newPassword")}
            type="password"
            fullWidth
            label="New password"
            placeholder="Min 8 chars, upper, lower, digit, special"
            error={errors.newPassword?.message}
            required
          />

          <Input
            {...register("confirmNewPassword")}
            type="password"
            fullWidth
            label="Confirm new password"
            placeholder="Confirm new password"
            error={errors.confirmNewPassword?.message}
            required
          />

          {apiError && (
            <p className="text-red text-sm" role="alert">
              {apiError}
            </p>
          )}

          <div className="flex gap-3 pt-2 justify-end">
            <Button
              type="button"
              onClick={handleClose}
              color="blackOutlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="small"
              loading={mutation.isLoading}
              disabled={mutation.isLoading}
              color="primary"
            >
              Update password
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
